import { useState, useEffect } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Building2,
  Users,
  Clock,
  CheckCircle2,
  XCircle,
  Wallet,
  Search,
  Filter,
  Trash2,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminWithdrawalsPage() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [creators, setCreators] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [processing, setProcessing] = useState(null);
  const [tab, setTab] = useState("withdrawals");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [creatorsRes, withdrawalsRes] = await Promise.all([
        axios.get(`${API}/admin/creators-iban`, { withCredentials: true }),
        axios.get(`${API}/admin/withdrawals`, { withCredentials: true }),
      ]);
      setCreators(creatorsRes.data);
      setWithdrawals(withdrawalsRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Admin access required");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(amount || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const handleUpdateStatus = async (withdrawalId, status) => {
    setProcessing(withdrawalId);
    try {
      await axios.put(`${API}/admin/withdrawals/${withdrawalId}`, { status }, { withCredentials: true });
      toast.success(status === "completed" ? t("adminWithdraw.markedPaid") : t("adminWithdraw.markedRejected"));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally {
      setProcessing(null);
    }
  };

  const handleDeleteIban = async (methodId) => {
    if (!window.confirm(t("adminWithdraw.confirmDeleteIban"))) return;
    setProcessing(methodId);
    try {
      await axios.delete(`${API}/admin/creators-iban/${methodId}`, { withCredentials: true });
      toast.success(t("adminWithdraw.ibanDeleted"));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally {
      setProcessing(null);
    }
  };

  const filteredWithdrawals = withdrawals.filter((w) => {
    if (filter !== "all" && w.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (w.user_email || "").toLowerCase().includes(q) ||
        (w.user_name || "").toLowerCase().includes(q) ||
        (w.method_details?.iban || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  const filteredCreators = creators.filter((c) => {
    if (search) {
      const q = search.toLowerCase();
      return (
        c.email.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.iban.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const pendingCount = withdrawals.filter((w) => w.status === "pending").length;
  const totalPending = withdrawals
    .filter((w) => w.status === "pending")
    .reduce((sum, w) => sum + (w.amount || 0), 0);

  if (loading) {
    return (
      <div className="space-y-4 p-4" data-testid="admin-withdrawals-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500" data-testid="admin-withdrawals-page">
      <div>
        <h1 className="text-2xl font-bold">{t("adminWithdraw.title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t("adminWithdraw.subtitle")}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card data-testid="stat-creators">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Users className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{creators.length}</p>
                <p className="text-xs text-muted-foreground">{t("adminWithdraw.creatorsWithIban")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-pending">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Clock className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingCount}</p>
                <p className="text-xs text-muted-foreground">{t("adminWithdraw.pendingRequests")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-pending-amount">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Wallet className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(totalPending)}</p>
                <p className="text-xs text-muted-foreground">{t("adminWithdraw.totalPending")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border pb-0">
        <button
          onClick={() => setTab("withdrawals")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
            tab === "withdrawals" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          data-testid="tab-withdrawals"
        >
          {t("adminWithdraw.withdrawals")} {pendingCount > 0 && (
            <Badge variant="destructive" className="ml-2 text-xs px-1.5 py-0">{pendingCount}</Badge>
          )}
        </button>
        <button
          onClick={() => setTab("ibans")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
            tab === "ibans" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          data-testid="tab-ibans"
        >
          {t("adminWithdraw.creatorsIban")}
        </button>
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("adminWithdraw.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-10 rounded-xl"
            data-testid="search-input"
          />
        </div>
        {tab === "withdrawals" && (
          <div className="flex gap-2">
            {["all", "pending", "completed", "rejected"].map((f) => (
              <Button
                key={f}
                variant={filter === f ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter(f)}
                className="rounded-lg text-xs"
                data-testid={`filter-${f}`}
              >
                {f === "all" ? t("adminWithdraw.all") :
                 f === "pending" ? t("adminWithdraw.pending") :
                 f === "completed" ? t("adminWithdraw.paid") :
                 t("adminWithdraw.rejected")}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Withdrawals Tab */}
      {tab === "withdrawals" && (
        <div className="space-y-3" data-testid="withdrawals-list">
          {filteredWithdrawals.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">{t("adminWithdraw.noWithdrawals")}</CardContent></Card>
          ) : (
            filteredWithdrawals.map((w) => (
              <Card key={w.id} className="overflow-hidden" data-testid={`withdrawal-${w.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-lg">{formatCurrency(w.amount)}</span>
                        <Badge
                          variant={w.status === "pending" ? "outline" : w.status === "completed" ? "default" : "destructive"}
                          className={`text-xs ${w.status === "pending" ? "border-amber-500 text-amber-600" : w.status === "completed" ? "bg-emerald-500" : ""}`}
                        >
                          {w.status === "pending" && <Clock className="h-3 w-3 mr-1" />}
                          {w.status === "completed" && <CheckCircle2 className="h-3 w-3 mr-1" />}
                          {w.status === "rejected" && <XCircle className="h-3 w-3 mr-1" />}
                          {w.status === "pending" ? t("adminWithdraw.pending") :
                           w.status === "completed" ? t("adminWithdraw.paid") :
                           t("adminWithdraw.rejected")}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                        <div><span className="text-muted-foreground">Email:</span> <span className="font-medium">{w.user_email || "-"}</span></div>
                        <div><span className="text-muted-foreground">{t("adminWithdraw.accountName")}:</span> <span className="font-medium">{w.method_details?.full_name || w.user_name || "-"}</span></div>
                        <div><span className="text-muted-foreground">IBAN:</span> <span className="font-mono text-xs font-medium">{w.method_details?.iban || "-"}</span></div>
                        <div><span className="text-muted-foreground">BIC:</span> <span className="font-mono text-xs">{w.method_details?.bic || "-"}</span></div>
                      </div>
                      <p className="text-xs text-muted-foreground">{formatDate(w.created_at)}</p>
                    </div>
                    {w.status === "pending" && (
                      <div className="flex gap-2 shrink-0">
                        <Button
                          size="sm"
                          onClick={() => handleUpdateStatus(w.id, "completed")}
                          disabled={processing === w.id}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                          data-testid={`mark-paid-${w.id}`}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1.5" />
                          {t("adminWithdraw.markPaid")}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateStatus(w.id, "rejected")}
                          disabled={processing === w.id}
                          className="text-destructive border-destructive/30 hover:bg-destructive/10 rounded-lg"
                          data-testid={`mark-rejected-${w.id}`}
                        >
                          <XCircle className="h-4 w-4 mr-1.5" />
                          {t("adminWithdraw.reject")}
                        </Button>
                      </div>
                    )}
                    {w.status !== "pending" && w.processed_at && (
                      <p className="text-xs text-muted-foreground shrink-0">
                        {t("adminWithdraw.processedOn")} {formatDate(w.processed_at)}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* IBANs Tab */}
      {tab === "ibans" && (
        <div className="space-y-3" data-testid="ibans-list">
          {filteredCreators.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">{t("adminWithdraw.noCreators")}</CardContent></Card>
          ) : (
            filteredCreators.map((c) => (
              <Card key={`${c.user_id}-${c.iban}`} data-testid={`creator-iban-${c.user_id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                          {(c.name || c.email)[0]?.toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{c.name || c.full_name}</p>
                          <p className="text-xs text-muted-foreground">{c.email}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm mt-3">
                        <div><span className="text-muted-foreground">{t("adminWithdraw.accountName")}:</span> <span className="font-medium">{c.full_name}</span></div>
                        <div><span className="text-muted-foreground">IBAN:</span> <span className="font-mono text-xs font-medium">{c.iban}</span></div>
                        <div><span className="text-muted-foreground">BIC:</span> <span className="font-mono text-xs">{c.bic}</span></div>
                        <div><span className="text-muted-foreground">{t("adminWithdraw.bank")}:</span> <span>{c.bank_name}</span></div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">{t("adminWithdraw.addedOn")} {formatDate(c.added_at)}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <div className="text-right space-y-1">
                        <p className="text-lg font-bold">{formatCurrency(c.available_balance)}</p>
                        <p className="text-xs text-muted-foreground">{t("adminWithdraw.availableBalance")}</p>
                        {c.pending_withdrawals > 0 && (
                          <Badge variant="outline" className="border-amber-500 text-amber-600 text-xs">
                            <Clock className="h-3 w-3 mr-1" />
                            {formatCurrency(c.pending_withdrawals)} {t("adminWithdraw.pending")}
                          </Badge>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteIban(c.method_id)}
                        disabled={processing === c.method_id}
                        className="mt-1 p-2 rounded-lg border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                        data-testid={`delete-iban-${c.method_id}`}
                        title={t("adminWithdraw.deleteIban")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}
